export const STORAGE_KEY = "dashboard_techniques_ui_state";

export const DEFAULT_TECHNIQUES_UI_STATE = {
  search: "",
  grade: "Toutes les ceintures",
  family: "Toutes les familles",
  uv2: "Toutes les fiches",
  showFav: false,
  sort: "beltFamily",
  perPage: 24,
};

export const FALLBACK_IMG = "/img/no-image.jpg";

export const collatorFR = new Intl.Collator("fr", {
  sensitivity: "base",
});

const BELT_BASE = [
  "blanche",
  "jaune",
  "orange",
  "verte",
  "bleue",
  "marron",
  "noire",
];

export const strip = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export function normalizeUV2(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  return String(value)
    .split(/[,\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function uniqSorted(values = []) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    collatorFR.compare(String(a), String(b))
  );
}

export function beltRank(ceinture = "") {
  const value = strip(ceinture);

  const danMatch = value.match(/(\d+)\s*(?:er|e)?\s*dan/);

  if (danMatch) {
    const dan = parseInt(danMatch[1], 10) || 1;
    return 6 + dan;
  }

  const foundColors = BELT_BASE.map((color, index) =>
    value.includes(color) ? index : -1
  ).filter((index) => index >= 0);

  if (foundColors.length === 1) {
    return foundColors[0];
  }

  if (foundColors.length >= 2) {
    return (
      foundColors.reduce((sum, index) => sum + index, 0) / foundColors.length
    );
  }

  const exactIndex = BELT_BASE.indexOf(value);

  if (exactIndex >= 0) {
    return exactIndex;
  }

  return Number.POSITIVE_INFINITY;
}

/**
 * Ici on ne force plus FAMILY_ORDER en dur.
 * On récupère l’ordre réel des familles depuis les techniques retournées par l’API.
 * Si ton back renvoie les techniques dans l’ordre souhaité, cet ordre sera conservé.
 */
export function extractFamilyOrder(techniques = []) {
  const seen = new Set();
  const order = [];

  techniques.forEach((technique) => {
    const family = String(technique?.famille || "").trim();

    if (!family) return;

    const key = strip(family);

    if (seen.has(key)) return;

    seen.add(key);
    order.push(family);
  });

  return order;
}

export function familyRank(family = "", familyOrder = []) {
  const current = strip(family);

  const index = familyOrder.findIndex((item) => strip(item) === current);

  return index >= 0 ? index : 9999;
}

export function buildDynamicOptions(techniques = []) {
  const grades = uniqSorted(
    techniques.map((technique) => technique?.ceinture).filter(Boolean)
  ).sort((a, b) => beltRank(a) - beltRank(b) || collatorFR.compare(a, b));

  const familyOrder = extractFamilyOrder(techniques);

  const uv2 = uniqSorted(
    techniques.flatMap((technique) => normalizeUV2(technique?.uv2))
  );

  return {
    grades: ["Toutes les ceintures", ...grades],
    families: ["Toutes les familles", ...familyOrder],
    uv2: ["Toutes les fiches", ...uv2],
    familyOrder,
  };
}

export function filterAndSortTechniques({
  techniques = [],
  ui = DEFAULT_TECHNIQUES_UI_STATE,
  favorites = [],
  familyOrder = [],
}) {
  const term = strip(ui.search);

  const favoriteSet = new Set(favorites);

  const filtered = techniques.filter((technique) => {
    const searchable = strip(
      [
        technique?.nom,
        technique?.traduction,
        technique?.famille,
        technique?.ceinture,
        technique?.uv2,
      ]
        .filter(Boolean)
        .join(" ")
    );

    const matchSearch = !term || searchable.includes(term);

    const matchGrade =
      ui.grade === DEFAULT_TECHNIQUES_UI_STATE.grade ||
      technique?.ceinture === ui.grade;

    const matchFamily =
      ui.family === DEFAULT_TECHNIQUES_UI_STATE.family ||
      technique?.famille === ui.family;

    const matchUV2 =
      ui.uv2 === DEFAULT_TECHNIQUES_UI_STATE.uv2 ||
      normalizeUV2(technique?.uv2).includes(ui.uv2);

    const matchFavorite = !ui.showFav || favoriteSet.has(technique.id);

    return (
      matchSearch && matchGrade && matchFamily && matchUV2 && matchFavorite
    );
  });

  return filtered.slice().sort((a, b) => {
    if (ui.sort === "alpha") {
      return collatorFR.compare(String(a?.nom || ""), String(b?.nom || ""));
    }

    if (ui.sort === "grade") {
      return (
        beltRank(a?.ceinture) - beltRank(b?.ceinture) ||
        collatorFR.compare(String(a?.nom || ""), String(b?.nom || ""))
      );
    }

    if (ui.sort === "family") {
      return (
        familyRank(a?.famille, familyOrder) -
          familyRank(b?.famille, familyOrder) ||
        collatorFR.compare(String(a?.nom || ""), String(b?.nom || ""))
      );
    }

    const beltDiff = beltRank(a?.ceinture) - beltRank(b?.ceinture);

    if (beltDiff !== 0) return beltDiff;

    const familyDiff =
      familyRank(a?.famille, familyOrder) -
      familyRank(b?.famille, familyOrder);

    if (familyDiff !== 0) return familyDiff;

    return collatorFR.compare(String(a?.nom || ""), String(b?.nom || ""));
  });
}

export function getStoredUserId() {
  try {
    const raw = localStorage.getItem("userInfo");
    const userInfo = raw ? JSON.parse(raw) : null;

    return userInfo?.userId || userInfo?.id || userInfo?.user_id || null;
  } catch {
    return null;
  }
}

export function readInitialUiState() {
  let stored = {};

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    stored = raw ? JSON.parse(raw) : {};
  } catch {
    stored = {};
  }

  let queryPatch = {};

  try {
    const params = new URLSearchParams(window.location.search);

    if (params.get("q")) queryPatch.search = params.get("q");
    if (params.get("grade")) queryPatch.grade = params.get("grade");
    if (params.get("famille")) queryPatch.family = params.get("famille");
    if (params.get("uv2")) queryPatch.uv2 = params.get("uv2");
    if (params.get("fav")) queryPatch.showFav = params.get("fav") === "1";
    if (params.get("sort")) queryPatch.sort = params.get("sort");
  } catch {
    queryPatch = {};
  }

  return {
    ...DEFAULT_TECHNIQUES_UI_STATE,
    ...stored,
    ...queryPatch,
    perPage: DEFAULT_TECHNIQUES_UI_STATE.perPage,
  };
}

export function createShareUrl(ui) {
  const params = new URLSearchParams();

  if (ui.search) params.set("q", ui.search);
  if (ui.grade !== DEFAULT_TECHNIQUES_UI_STATE.grade) {
    params.set("grade", ui.grade);
  }
  if (ui.family !== DEFAULT_TECHNIQUES_UI_STATE.family) {
    params.set("famille", ui.family);
  }
  if (ui.uv2 !== DEFAULT_TECHNIQUES_UI_STATE.uv2) {
    params.set("uv2", ui.uv2);
  }
  if (ui.showFav) params.set("fav", "1");
  if (ui.sort !== DEFAULT_TECHNIQUES_UI_STATE.sort) {
    params.set("sort", ui.sort);
  }

  const query = params.toString();

  return `${window.location.origin}${window.location.pathname}${
    query ? `?${query}` : ""
  }`;
}

export function beltClasses(ceinture = "") {
  const value = strip(ceinture);

  if (value.includes("jaune")) {
    return "bg-yellow-400 text-slate-950 ring-yellow-300";
  }

  if (value.includes("orange")) {
    return "bg-orange-400 text-slate-950 ring-orange-300";
  }

  if (value.includes("verte")) {
    return "bg-green-500 text-white ring-green-400";
  }

  if (value.includes("bleue")) {
    return "bg-blue-600 text-white ring-blue-500";
  }

  if (value.includes("marron")) {
    return "bg-amber-900 text-white ring-amber-800";
  }

  if (value.includes("noire") || value.includes("dan")) {
    return "bg-neutral-950 text-white ring-neutral-800";
  }

  if (value.includes("blanche")) {
    return "bg-white text-slate-900 ring-slate-200";
  }

  return "bg-slate-200 text-slate-800 ring-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-600";
}

export function headerGradient(ceinture = "") {
  const value = strip(ceinture);

  if (value.includes("jaune")) {
    return "from-amber-700 via-amber-500 to-yellow-400";
  }

  if (value.includes("orange")) {
    return "from-orange-800 via-orange-600 to-orange-400";
  }

  if (value.includes("verte")) {
    return "from-emerald-900 via-emerald-700 to-emerald-500";
  }

  if (value.includes("bleue")) {
    return "from-blue-950 via-blue-700 to-blue-500";
  }

  if (value.includes("marron")) {
    return "from-amber-950 via-amber-900 to-amber-700";
  }

  if (value.includes("noire") || value.includes("dan")) {
    return "from-neutral-950 via-neutral-800 to-neutral-600";
  }

  return "from-[#013E8E] via-indigo-600 to-[#4CA82F]";
}

export const hasJapaneseChars = (value = "") =>
  /[\u3040-\u30FF\u4E00-\u9FFF]/.test(value);

export function pickVoice(langStartsWith = "ja", fallbackLang = "ja-JP") {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    return { voice: null, lang: fallbackLang };
  }

  const voices = window.speechSynthesis.getVoices?.() || [];

  const voice = voices.find((item) =>
    item.lang?.toLowerCase().startsWith(langStartsWith)
  );

  return {
    voice: voice || null,
    lang: voice?.lang || fallbackLang,
  };
}

export function speakJapanese(text) {
  try {
    if (typeof window === "undefined") return;
    if (!window.speechSynthesis || !text) return;

    window.speechSynthesis.cancel();

    const { voice, lang } = pickVoice("ja", "ja-JP");
    const utterance = new SpeechSynthesisUtterance(text);

    if (voice) utterance.voice = voice;

    utterance.lang = lang;
    utterance.rate = 1;
    utterance.pitch = 1;

    window.speechSynthesis.speak(utterance);
  } catch {
    // speechSynthesis non disponible
  }
}